import apiClient from '@/lib/api-client';

/**
 * Taal-neutrale invoice DTO exactly as returned by the h12-1 BFF module (invoices.service.ts).
 * ISO dates, numeric amounts + currency code, null (never undefined) for absent values.
 */
export interface Invoice {
  id: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  amountExclTax: number | null;
  amountInclTax: number | null;
  taxAmount: number | null;
  amountPaid: number | null;
  currency: 'EUR';
  status: 'paid' | 'open' | 'unknown';
  overdue: boolean | null;
  period: number | null;
  description: string | null;
  reference: string | null;
  type: 'invoice' | 'credit';
}

export interface InvoiceListMeta {
  total?: number;
  page?: number;
  pageSize?: number;
  noBillingLink?: boolean;
  degraded?: boolean;
  /** Display-only — the caller's OWN resolved company. Never a filter key. */
  companyName?: string;
  /** Display-only, non-unique debtor number — never a filter key (h12-0 contract). */
  debtorNumber?: number | null;
}

export interface InvoiceListEnvelope {
  data: Invoice[] | null;
  meta: InvoiceListMeta;
}

export interface InvoiceListParams {
  year?: number;
  status?: 'all' | 'paid' | 'open';
  page?: number;
  pageSize?: number;
}

/**
 * Lists invoices for the authenticated caller. Tenant identity is derived server-side from
 * the session (never sent by the client) — this call intentionally carries no gln/companyId.
 */
export async function getInvoices(params: InvoiceListParams = {}): Promise<InvoiceListEnvelope> {
  const query = new URLSearchParams();
  if (params.year != null) query.set('year', String(params.year));
  if (params.status != null) query.set('status', params.status);
  query.set('page', String(params.page ?? 1));
  query.set('pageSize', String(params.pageSize ?? 20));

  const response = await apiClient.get<InvoiceListEnvelope>(`/invoices?${query.toString()}`);
  return response.data;
}

/** Downloads a single invoice PDF as a Blob (503 while the Exact source is gated — FR54). */
export async function downloadInvoicePdf(invoiceId: string): Promise<Blob> {
  const response = await apiClient.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
  return response.data as Blob;
}

/** Downloads a ZIP of the selected invoices (503 while the Exact source is gated — FR54). */
export async function downloadInvoicesZip(invoiceIds: string[]): Promise<Blob> {
  const response = await apiClient.post(
    '/invoices/download-zip',
    { invoiceIds },
    { responseType: 'blob' },
  );
  return response.data as Blob;
}
